// Keeps the maker and the approvers in step without either of them refreshing.
//
// Polls GET /supervision/pulse — one indexed read per side plus two counts — every
// POLL_MS while the tab is visible, and at once on focus. When the stamp or a
// count moves it announces a change (screens re-read) and refreshes the bell. A
// decision on one of this user's own requests becomes a toast, so an operator
// hears "approved" where they are working rather than on a page they may never
// open. Mounted once in Layout, only when the `supervision` flag is on.
//
// A poll, not a held-open stream: Cloud Run bills a streaming connection for as
// long as it is open, so one per open tab would keep instances running all day.
import { useCallback, useEffect, useRef } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { API, SUPERVISION_HOLD_EVENT } from '../../context/AuthContext';
import {
  NOTIFICATIONS_REFRESH_EVENT,
  SUPERVISION_CHANGED_EVENT,
  announceSupervisionChange,
} from '../../utils/supervisionSync';

const POLL_MS = 15000;

export default function SupervisionPulse() {
  const navigate = useNavigate();
  const baseline = useRef(null);
  const since = useRef(null);
  const inflight = useRef(false);
  const toasted = useRef(new Set());

  const poll = useCallback(async ({ announce = true } = {}) => {
    if (inflight.current) return;
    if (typeof document !== 'undefined' && document.hidden) return;
    inflight.current = true;
    try {
      const { data } = await axios.get(`${API}/supervision/pulse`, {
        params: since.current ? { since: since.current } : {},
      });
      const prev = baseline.current;
      const moved = prev && (
        prev.stamp !== data.stamp
        || prev.mine_pending !== data.mine_pending
        || prev.queue_pending !== data.queue_pending
      );
      baseline.current = {
        stamp: data.stamp, mine_pending: data.mine_pending, queue_pending: data.queue_pending,
      };
      if (data.stamp) since.current = data.stamp;

      if (moved) {
        window.dispatchEvent(new Event(NOTIFICATIONS_REFRESH_EVENT));
        if (announce) {
          window.dispatchEvent(new CustomEvent(SUPERVISION_CHANGED_EVENT, { detail: { local: false } }));
        }
      }

      (data.decisions || []).forEach((d) => {
        if (!d?.id || toasted.current.has(d.id)) return;
        toasted.current.add(d.id);
        const what = [d.action_label, d.entity_code].filter(Boolean).join(' — ') || 'your change';
        const opts = {
          description: d.decision_note || (d.decided_by_name ? `By ${d.decided_by_name}` : undefined),
          action: { label: 'View', onClick: () => navigate('/my-change-requests') },
          duration: 8000,
        };
        if (d.status === 'approved') toast.success(`Approved: ${what}`, opts);
        else if (d.status === 'rejected') toast.error(`Rejected: ${what}`, opts);
        else toast.warning(`Could not be applied: ${what}`, opts);
      });
    } catch (_err) {
      // A missed beat is retried on the next tick; nothing on screen depends on it.
    } finally {
      inflight.current = false;
    }
  }, [navigate]);

  useEffect(() => {
    poll({ announce: false });
    const tick = window.setInterval(() => poll(), POLL_MS);
    const onFocus = () => poll();
    const onVisible = () => { if (!document.hidden) poll(); };
    // A change made in this tab has already told the screens; just move the
    // baseline (and the bell) so the next tick does not announce it twice.
    const onLocal = (event) => { if (event?.detail?.local) poll({ announce: false }); };
    // A save held by the gate is a new request on both sides.
    const onHold = () => announceSupervisionChange({ reason: 'held' });

    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener(SUPERVISION_CHANGED_EVENT, onLocal);
    window.addEventListener(SUPERVISION_HOLD_EVENT, onHold);
    return () => {
      window.clearInterval(tick);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener(SUPERVISION_CHANGED_EVENT, onLocal);
      window.removeEventListener(SUPERVISION_HOLD_EVENT, onHold);
    };
  }, [poll]);

  return null;
}
